import{contractFactory as O,projectFactory as j,ClarigenClient as V}from"@clarigen/core";import{asciiToBytes as m}from"micro-stacks/common";import{convertNameBuff as Z,getContractParts as v,project as z,contracts as H}from"@bns-x/core";var C="https://api.bns.xyz";function x(r=!0){return r?"SP000000000000000000002Q6VF78":"ST000000000000000000002AMW42H"}var S=`;; Source code for the name wrapper contract.
;; 
;; This contract is not meant to be deployed as a standalone contract in
;; the BNSx protocol. Instead, it is deployed for each individual name that
;; is upgraded to BNSx.
;; 
;; The purpose of this contract is to own a BNS name, and only allow
;; owners of the equivalent name on BNSx to control the legacy name.
;; 
;; For example, if a wrapper contract owns \`name.btc\`, and Alice owns \`name.btc\`
;; on BNSx, then only Alice can interact with this contract.

(define-constant ERR_NO_NAME (err u10000))
(define-constant ERR_NAME_TRANSFER (err u10001))
(define-constant ERR_UNAUTHORIZED (err u10002))
(define-constant ERR_NOT_WRAPPED (err u10003))

(define-data-var wrapper-id-var (optional uint) none)

;; Unwrap the BNS name from this contract.
;; 
;; When unwrapping, the BNSx name is burned. This ensures that there is a 1-to-1
;; mapping between BNSx and BNS names.
;; 
;; @throws if called by anyone other than the BNSx name owner
;; 
;; @param recipient; the name owner can optionally transfer the BNS name to
;; a different account. If \`none\`, recipient defauls to \`tx-sender\`.
(define-public (unwrap (recipient (optional principal)))
  (let
    (
      (props (try! (get-name-info)))
      (new-owner (default-to tx-sender recipient))
      (owner (get owner props))
    )
    (asserts! (is-eq tx-sender owner) ERR_UNAUTHORIZED)
    (try! (contract-call? 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM.bnsx-registry burn (get id props)))
    (unwrap! (as-contract (contract-call? 'ST000000000000000000002AMW42H.bns name-transfer (get namespace props) (get name props) new-owner none)) ERR_NAME_TRANSFER)
    (ok props)
  )
)

;; Helper method to fetch the BNS name owned by this contract.
(define-read-only (get-own-name)
  (ok (unwrap! (contract-call? 'ST000000000000000000002AMW42H.bns resolve-principal (as-contract tx-sender)) ERR_NO_NAME))
)

;; Helper method to fetch information about the BNSx name that is equivalent to the
;; BNS name owned by this contract. For example, if this contract owns \`name.btc\`,
;; it returns the properties of \`name.btc\` on BNSx.
(define-read-only (get-name-info)
  (let
    (
      (name (try! (get-own-name)))
      (props (unwrap! (contract-call? 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM.bnsx-registry get-name-properties name) ERR_NOT_WRAPPED))
    )
    (ok props)
  )
)

;; Helper method to return the owner of the BNSx name that is equivalent to this
;; contract's legacy name
(define-read-only (get-owner)
  (ok (get owner (try! (get-name-info))))
)

;; Helper method to interact with BNS to update the zonefile for this name
;; 
;; @throws if called by anyone other than the BNSx name owner
(define-public (name-update (namespace (buff 20)) (name (buff 48)) (zonefile-hash (buff 20)))
  (let
    (
      (props (try! (get-name-info)))
    )
    (asserts! (is-eq tx-sender (get owner props)) ERR_UNAUTHORIZED)
    (asserts! (is-eq (get namespace props) namespace) ERR_UNAUTHORIZED)
    (asserts! (is-eq (get name props) name) ERR_UNAUTHORIZED)
    (match (as-contract (contract-call? 'ST000000000000000000002AMW42H.bns name-update namespace name zonefile-hash))
      r (ok true)
      e (err (to-uint e))
    )
  )
)

;; Helper method to interact with BNS to renew the name
;; 
;; @param stx-to-burn; the number of STX to burn to renew the name
(define-public (name-renewal (stx-to-burn uint))
  (let
    (
      (props (try! (get-name-info)))
    )
    (asserts! (is-eq tx-sender (get owner props)) ERR_UNAUTHORIZED)
    (match (as-contract (contract-call? 'ST000000000000000000002AMW42H.bns name-renewal
      (get namespace props)
      (get name props)
      stx-to-burn
      none
      none
    ))
      r (ok true)
      e (err (to-uint e))
    )
  )
)

;; Allow BNSx name owner to withdraw any NFTs that were sent to this contract
(define-public (withdraw-nft (nft <nft-trait>) (token-id uint) (recipient principal))
  (begin
    ;; #[filter(nft)]
    (try! (validate-owner))
    (as-contract (contract-call? nft transfer token-id tx-sender recipient))
  )
)

;; Allow BNSx name owner to withdraw any fungible tokens that were sent to this contract
(define-public (withdraw-ft (ft <ft-trait>) (amount uint) (recipient principal))
  (begin
    ;; #[filter(ft)]
    (try! (validate-owner))
    (as-contract (contract-call? ft transfer amount tx-sender recipient none))
  )
)

;; Allow BNSx name owner to withdraw any STX that were sent to this contract
(define-public (withdraw-stx (amount uint) (recipient principal))
  (begin
    ;; #[filter(amount, recipient)]
    (try! (validate-owner))
    (as-contract (stx-transfer? amount tx-sender recipient))
  )
)

(define-read-only (get-wrapper-id)
  (var-get wrapper-id-var)
)

(define-private (register-self)
  (let
    (
      (self (as-contract tx-sender))
      (id (try! (contract-call? 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM.wrapper-migrator register-wrapper self)))
    )
    (var-set wrapper-id-var (some id))
    (ok id)
  )
)

(define-private (validate-owner)
  (let
    (
      (props (try! (get-name-info)))
    )
    (asserts! (is-eq tx-sender (get owner props)) ERR_UNAUTHORIZED)
    (ok true)
  )
)

(try! (register-self))

(define-trait nft-trait
  (
    (get-last-token-id () (response uint uint))
    (get-token-uri (uint) (response (optional (string-ascii 256)) uint))
    (get-owner (uint) (response (optional principal) uint))
    (transfer (uint principal principal) (response bool uint))
  )
)
(define-trait ft-trait
  (
    (transfer (uint principal principal (optional (buff 34))) (response bool uint))
    (get-name () (response (string-ascii 32) uint))
    (get-symbol () (response (string-ascii 32) uint))
    (get-decimals () (response uint uint))
    (get-balance (principal) (response uint uint))
    (get-total-supply () (response uint uint))
    (get-token-uri () (response (optional (string-utf8 256)) uint))
  )
)`,W=[`;; Source code for the name wrapper contract.
;; 
;; This contract is not meant to be deployed as a standalone contract in
;; the BNSx protocol. Instead, it is deployed for each individual name that
;; is upgraded to BNSx.
;; 
;; The purpose of this contract is to own a BNS legacy name, and only allow
;; owners of the equivalent name on BNSx to control the legacy name.
;; 
;; For example, if a wrapper contract owns \`name.btc\`, and Alice owns \`name.btc\`
;; on BNSx, then only Alice can interact with this contract.

(define-constant ERR_NO_NAME (err u10000))
(define-constant ERR_NAME_TRANSFER (err u10001))
(define-constant ERR_UNAUTHORIZED (err u10002))
(define-constant ERR_NOT_WRAPPED (err u10003))

(define-data-var wrapper-id-var (optional uint) none)

;; Unwrap the legacy BNS name from this contract.
;; 
;; When unwrapping, the BNSx name is burned. This ensures that there is a 1-to-1
;; mapping between BNSx and BNS legacy names.
;; 
;; @throws if called by anyone other than the BNSx name owner
;; 
;; @param recipient; the name owner can optionally transfer the BNS legacy name to
;; a different account. If \`none\`, recipient defauls to \`tx-sender\`.
(define-public (unwrap (recipient (optional principal)))
  (let
    (
      (props (try! (get-name-info)))
      (new-owner (default-to tx-sender recipient))
      (owner (get owner props))
    )
    (asserts! (is-eq tx-sender owner) ERR_UNAUTHORIZED)
    (try! (contract-call? 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM.bnsx-registry burn (get id props)))
    (unwrap! (as-contract (contract-call? 'ST000000000000000000002AMW42H.bns name-transfer (get namespace props) (get name props) new-owner none)) ERR_NAME_TRANSFER)
    (ok props)
  )
)

;; Helper method to fetch the BNS legacy name owned by this contract.
(define-read-only (get-own-name)
  (ok (unwrap! (contract-call? 'ST000000000000000000002AMW42H.bns resolve-principal (as-contract tx-sender)) ERR_NO_NAME))
)

;; Helper method to fetch information about the BNSx name that is equivalent to the
;; legacy name owned by this contract. For example, if this contract owns \`name.btc\`,
;; it returns the properties of \`name.btc\` on BNSx.
(define-read-only (get-name-info)
  (let
    (
      (name (try! (get-own-name)))
      (props (unwrap! (contract-call? 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM.bnsx-registry get-name-properties name) ERR_NOT_WRAPPED))
    )
    (ok props)
  )
)

;; Helper method to return the owner of the BNSx name that is equivalent to this
;; contract's legacy name
(define-read-only (get-owner)
  (ok (get owner (try! (get-name-info))))
)

;; Helper method to interact with legacy BNS to update the zonefile for this name
;; 
;; @throws if called by anyone other than the BNSx name owner
(define-public (name-update (namespace (buff 20)) (name (buff 48)) (zonefile-hash (buff 20)))
  (let
    (
      (props (try! (get-name-info)))
    )
    (asserts! (is-eq tx-sender (get owner props)) ERR_UNAUTHORIZED)
    (asserts! (is-eq (get namespace props) namespace) ERR_UNAUTHORIZED)
    (asserts! (is-eq (get name props) name) ERR_UNAUTHORIZED)
    (match (as-contract (contract-call? 'ST000000000000000000002AMW42H.bns name-update namespace name zonefile-hash))
      r (ok true)
      e (err (to-uint e))
    )
  )
)

(define-read-only (get-wrapper-id)
  (var-get wrapper-id-var)
)

(define-private (register-self)
  (let
    (
      (self (as-contract tx-sender))
      (id (try! (contract-call? 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM.wrapper-migrator register-wrapper self)))
    )
    (var-set wrapper-id-var (some id))
    (ok id)
  )
)

(try! (register-self))`],B=[S,...W];var D=class{contracts;networkKey;client;constructor(e="mainnet",t="https://stacks-node-api.mainnet.stacks.co"){this.networkKey=e,this.contracts=j(z,e),this.client=new V(t)}get isMainnet(){return this.networkKey==="mainnet"}get registry(){return this.contracts.bnsxRegistry,this.contracts.bnsxRegistry}get nameNftAsset(){let e=this.registry,t=e.non_fungible_tokens[0].name;return`${e.identifier}::${t}`}get queryHelper(){return this.contracts.queryHelper}get nameWrapperCode(){return this.convertNameWrapperCode(S)}get validNameWrappers(){return B.map(e=>this.convertNameWrapperCode(e))}isValidNameWrapper(e){return typeof this.validNameWrappers.find(n=>n===e)<"u"}convertNameWrapperCode(e){let t=this.registry.identifier,[n]=v(t),s="ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM";return e.replaceAll(s,n).replaceAll("ST000000000000000000002AMW42H",x(this.isMainnet))}get legacyBns(){return this.bnsCore}get bnsCore(){let e=H.bnsV1;return O(e,`${x(this.isMainnet)}.bns`)}get migrator(){return this.contracts.wrapperMigrator}get upgrader(){return this.migrator}nameWrapper(e){let t=H.nameWrapperV2;return O(t,e)}async resolveName(e){let[t,n]=v(e).map(m),s=this.registry.getNameProperties({name:t,namespace:n}),o=await this.client.ro(s,{tip:"latest",json:!0});if(!o)return null;let{id:i,owner:a,...p}=o;return{id:Number(i),owner:a}}async reverseResolve(e){let t=await this.client.ro(this.registry.getPrimaryName(e),{latest:!0});return t===null?null:Z(t)}async computeNamePrice(e,t){let n=await this.client.ro(this.bnsCore.getNamePrice({name:m(e),namespace:m(t)}));if(!n.isOk)throw new Error(`Unable to get name price: received error ${n.value}`);return n.value}async fetchNamespaceProperties(e){let t=m(e),n=await this.client.ro(this.bnsCore.getNamespaceProperties(t));if(n.isOk)return n.value.properties;throw new Error(`\`fetchNamespaceProperties\`: ${e} does not exist, returned error ${n.value}`)}async fetchNamespaceExpiration(e){return(await this.fetchNamespaceProperties(e)).lifetime}};var h=class{constructor(e){this.config=e}};import I from"axios";import k from"form-data";var u=class extends Error{url;status;statusText;body;request;constructor(e,t,n){super(n),this.name="ApiError",this.url=t.url,this.status=t.status,this.statusText=t.statusText,this.body=t.body,this.request=e}};var E=class extends Error{constructor(e){super(e),this.name="CancelError"}get isCancelled(){return!0}},f=class{[Symbol.toStringTag];_isResolved;_isRejected;_isCancelled;_cancelHandlers;_promise;_resolve;_reject;constructor(e){this._isResolved=!1,this._isRejected=!1,this._isCancelled=!1,this._cancelHandlers=[],this._promise=new Promise((t,n)=>{this._resolve=t,this._reject=n;let s=a=>{var p;this._isResolved||this._isRejected||this._isCancelled||(this._isResolved=!0,(p=this._resolve)==null||p.call(this,a))},o=a=>{var p;this._isResolved||this._isRejected||this._isCancelled||(this._isRejected=!0,(p=this._reject)==null||p.call(this,a))},i=a=>{this._isResolved||this._isRejected||this._isCancelled||this._cancelHandlers.push(a)};return Object.defineProperty(i,"isResolved",{get:()=>this._isResolved}),Object.defineProperty(i,"isRejected",{get:()=>this._isRejected}),Object.defineProperty(i,"isCancelled",{get:()=>this._isCancelled}),e(s,o,i)})}then(e,t){return this._promise.then(e,t)}catch(e){return this._promise.catch(e)}finally(e){return this._promise.finally(e)}cancel(){var e;if(!(this._isResolved||this._isRejected||this._isCancelled)){if(this._isCancelled=!0,this._cancelHandlers.length)try{for(let t of this._cancelHandlers)t()}catch(t){console.warn("Cancellation threw an error",t);return}this._cancelHandlers.length=0,(e=this._reject)==null||e.call(this,new E("Request aborted"))}}get isCancelled(){return this._isCancelled}};var _=r=>r!=null,y=r=>typeof r=="string",P=r=>y(r)&&r!=="",F=r=>typeof r=="object"&&typeof r.type=="string"&&typeof r.stream=="function"&&typeof r.arrayBuffer=="function"&&typeof r.constructor=="function"&&typeof r.constructor.name=="string"&&/^(Blob|File)$/.test(r.constructor.name)&&/^(Blob|File)$/.test(r[Symbol.toStringTag]),L=r=>r instanceof k,$=r=>r>=200&&r<300,Q=r=>{try{return btoa(r)}catch{return Buffer.from(r).toString("base64")}},X=r=>{let e=[],t=(s,o)=>{e.push(`${encodeURIComponent(s)}=${encodeURIComponent(String(o))}`)},n=(s,o)=>{_(o)&&(Array.isArray(o)?o.forEach(i=>{n(s,i)}):typeof o=="object"?Object.entries(o).forEach(([i,a])=>{n(`${s}[${i}]`,a)}):t(s,o))};return Object.entries(r).forEach(([s,o])=>{n(s,o)}),e.length>0?`?${e.join("&")}`:""},J=(r,e)=>{let t=r.ENCODE_PATH||encodeURI,n=e.url.replace("{api-version}",r.VERSION).replace(/{(.*?)}/g,(o,i)=>{var a;return(a=e.path)!=null&&a.hasOwnProperty(i)?t(String(e.path[i])):o}),s=`${r.BASE}${n}`;return e.query?`${s}${X(e.query)}`:s},Y=r=>{if(r.formData){let e=new k,t=(n,s)=>{y(s)||F(s)?e.append(n,s):e.append(n,JSON.stringify(s))};return Object.entries(r.formData).filter(([n,s])=>_(s)).forEach(([n,s])=>{Array.isArray(s)?s.forEach(o=>t(n,o)):t(n,s)}),e}},g=async(r,e)=>typeof e=="function"?e(r):e,K=async(r,e,t)=>{let n=await g(e,r.TOKEN),s=await g(e,r.USERNAME),o=await g(e,r.PASSWORD),i=await g(e,r.HEADERS),a=typeof(t==null?void 0:t.getHeaders)=="function"&&(t==null?void 0:t.getHeaders())||{},p=Object.entries({Accept:"application/json",...i,...e.headers,...a}).filter(([c,l])=>_(l)).reduce((c,[l,T])=>({...c,[l]:String(T)}),{});if(P(n)&&(p.Authorization=`Bearer ${n}`),P(s)&&P(o)){let c=Q(`${s}:${o}`);p.Authorization=`Basic ${c}`}return e.body&&(e.mediaType?p["Content-Type"]=e.mediaType:F(e.body)?p["Content-Type"]=e.body.type||"application/octet-stream":y(e.body)?p["Content-Type"]="text/plain":L(e.body)||(p["Content-Type"]="application/json")),p},ee=r=>{if(r.body)return r.body},te=async(r,e,t,n,s,o,i)=>{let a=I.CancelToken.source(),p={url:t,headers:o,data:n??s,method:e.method,withCredentials:r.WITH_CREDENTIALS,cancelToken:a.token};i(()=>a.cancel("The user aborted a request."));try{return await I.request(p)}catch(c){let l=c;if(l.response)return l.response;throw c}},re=(r,e)=>{if(e){let t=r.headers[e];if(y(t))return t}},ne=r=>{if(r.status!==204)return r.data},se=(r,e)=>{let n={400:"Bad Request",401:"Unauthorized",403:"Forbidden",404:"Not Found",500:"Internal Server Error",502:"Bad Gateway",503:"Service Unavailable",...r.errors}[e.status];if(n)throw new u(r,e,n);if(!e.ok)throw new u(r,e,"Generic Error")},U=(r,e)=>new f(async(t,n,s)=>{try{let o=J(r,e),i=Y(e),a=ee(e),p=await K(r,e,i);if(!s.isCancelled){let c=await te(r,e,o,a,i,p,s),l=ne(c),T=re(c,e.responseHeader),q={url:o,ok:$(c.status),status:c.status,statusText:c.statusText,body:T??l};se(e,q),t(q.body)}}catch(o){n(o)}});var R=class extends h{constructor(e){super(e)}request(e){return U(this.config,e)}};var b=class{constructor(e){this.httpRequest=e}getV1Names(e){return this.httpRequest.request({method:"GET",url:"/v1/names/{fqn}",path:{fqn:e},errors:{404:"Default Response",500:"Default Response"}})}getV1AddressesStacks(e){return this.httpRequest.request({method:"GET",url:"/v1/addresses/stacks/{principal}",path:{principal:e},errors:{404:"Default Response",500:"Default Response"}})}};var w=class{constructor(e){this.httpRequest=e}getBnsAddressesStacks(e){return this.httpRequest.request({method:"GET",url:"/bns/addresses/stacks/{principal}",path:{principal:e},errors:{404:"Default Response",500:"Default Response"}})}getBnsTotalNames(){return this.httpRequest.request({method:"GET",url:"/bns/total-names",errors:{404:"Default Response",500:"Default Response"}})}};var N=class{constructor(e){this.httpRequest=e}getL1InscribedNames(e){return this.httpRequest.request({method:"GET",url:"/l1/inscribed-names",query:{cursor:e}})}getL1InscriptionByName(e){return this.httpRequest.request({method:"GET",url:"/l1/inscription-by-name/{name}",path:{name:e},errors:{404:"Default Response"}})}getL1NameByInscription(e){return this.httpRequest.request({method:"GET",url:"/l1/name-by-inscription/{inscriptionId}",path:{inscriptionId:e},errors:{404:"Default Response"}})}getL1TotalL1Names(){return this.httpRequest.request({method:"GET",url:"/l1/total-l1-names"})}};var A=class{constructor(e){this.httpRequest=e}postTxBroadcast(e){return this.httpRequest.request({method:"POST",url:"/tx/broadcast",body:e,mediaType:"application/json"})}};var d=class{backwardsCompatible;bns;l1;utilities;request;constructor(e,t=R){this.request=new t({BASE:(e==null?void 0:e.BASE)??"https://api.bns.xyz",VERSION:(e==null?void 0:e.VERSION)??"0.1.0",WITH_CREDENTIALS:(e==null?void 0:e.WITH_CREDENTIALS)??!1,CREDENTIALS:(e==null?void 0:e.CREDENTIALS)??"include",TOKEN:e==null?void 0:e.TOKEN,USERNAME:e==null?void 0:e.USERNAME,PASSWORD:e==null?void 0:e.PASSWORD,HEADERS:e==null?void 0:e.HEADERS,ENCODE_PATH:e==null?void 0:e.ENCODE_PATH}),this.backwardsCompatible=new b(this.request),this.bns=new w(this.request),this.l1=new N(this.request),this.utilities=new A(this.request)}};var G=class{openapi;constructor(e=C){this.openapi=new d({BASE:e})}async getNamesOwnedByAddress(e){return this.openapi.bns.getBnsAddressesStacks(e)}async getDisplayName(e){let{names:t}=await this.openapi.backwardsCompatible.getV1AddressesStacks(e);return t[0]??null}async getCoreName(e){return(await this.getNamesOwnedByAddress(e)).coreName}async getNameDetails(e,t){let n=t?`${e}.${t}`:e;return this.getNameDetailsFromFqn(n)}async getNameDetailsFromFqn(e){try{return await this.openapi.backwardsCompatible.getV1Names(e)}catch{return null}}};export*from"@bns-x/core";import{parseZoneFile as oe}from"@fungible-systems/zone-file";var ie=(t=>(t.BTC_ADDR="_btc._addr",t.NOSTR="_._nostr",t))(ie||{}),ae=(t=>(t.REDIRECT="_redirect",t.LNURL="_lnaddr_redirect",t))(ae||{}),M=class{zoneFileText;zoneFile;constructor(e){this.zoneFileText=e??"";try{let t=oe(e);this.zoneFile=t}catch{this.zoneFile={}}}txtRecordValue(e){return(typeof e=="string"?e:e[0])??null}getTxtRecord(e){var n;let t=null;return(n=this.zoneFile.txt)==null||n.forEach(s=>{s.name===e&&(t=this.txtRecordValue(s.txt))}),t}getUriRecord(e){var n;let t=null;return(n=this.zoneFile.uri)==null||n.forEach(s=>{s.name===e&&(t=this.txtRecordValue(s.target))}),t}get btcAddr(){return this.getTxtRecord("_btc._addr")}get nostr(){return this.getTxtRecord("_._nostr")}get redirect(){return this.getUriRecord("_redirect")}};export*from"@bns-x/punycode";export{G as BnsApiClient,D as BnsContractsClient,M as ZoneFile,ie as ZonefileTxtKeys,ae as ZonefileUriKeys};
//# sourceMappingURL=index.mjs.map